<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("googleBusiness.locations") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="syncLocations" :loading="syncing">
                    <span class="material-icons" style="font-size: 16px; margin-right: 4px;">sync</span>
                    {{ $t("googleBusiness.sync") }}
                </a-button>
                <a-button @click="fetchLocations" :loading="loading">
                    <a-icon type="reload" /> {{ $t("googleBusiness.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="locations-container gbr-location-list" v-if="!loading">
            <div class="gbr-location-list__card" v-for="location in locations" :key="location.locationId">
                <div class="gbr-location-list__icon">
                    <span class="material-icons">store</span>
                </div>
                <div class="gbr-location-list__info">
                    <h3 class="gbr-location-list__name">{{ location.name }}</h3>
                    <div class="gbr-location-list__address" v-if="location.address">
                        <span class="material-icons">location_on</span>
                        <span class="gbr-location-list__address-text">{{ location.address }}</span>
                    </div>
                    <div class="gbr-location-list__meta">
                        <span v-if="location.phoneNumber" class="gbr-location-list__meta-item">
                            <span class="material-icons">phone</span>
                            <span>{{ location.phoneNumber }}</span>
                        </span>
                        <span v-if="location.averageRating" class="gbr-location-list__meta-item">
                            <span class="material-icons">star</span>
                            <span>{{ location.averageRating.toFixed(1) }}</span>
                        </span>
                        <span v-if="location.totalReviewCount" class="gbr-location-list__meta-item">
                            <span class="material-icons">reviews</span>
                            <span>{{ location.totalReviewCount }} reviews</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && locations.length === 0">
            <a-empty :description="$t('googleBusiness.noLocationsFound')" />
        </div>
        <div class="pagination" v-if="totalPages > 1">
            <a-pagination
                :current="page"
                :total="total"
                :page-size="limit"
                @change="onPageChange"
            />
        </div>
    </div>
</template>

<script>
import { GOOGLE_BUSINESS_API } from "../../../../../api/google-business";

export default {
    name: "LocationList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            locations: [],
            loading: false,
            syncing: false,
            page: 1,
            limit: 12,
            total: 0,
            totalPages: 0,
        };
    },
    methods: {
        async fetchLocations() {
            this.loading = true;
            try {
                const response = await GOOGLE_BUSINESS_API.listLocations(this.channel._id, this.page, this.limit);
                this.locations = response.data.locations;
                this.total = response.data.total;
                this.totalPages = response.data.totalPages;
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorFetchingLocations"));
            } finally {
                this.loading = false;
            }
        },
        async syncLocations() {
            this.syncing = true;
            try {
                await GOOGLE_BUSINESS_API.syncLocations(this.channel._id);
                this.$message.success(this.$t("googleBusiness.locationsSynced"));
                await this.fetchLocations();
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorSyncingLocations"));
            } finally {
                this.syncing = false;
            }
        },
        onPageChange(page) {
            this.page = page;
            this.fetchLocations();
        },
    },
    mounted() {
        this.fetchLocations();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .locations-container {
        margin-top: 15px;
    }

    /* İzole location listesi: dışarıyı etkilemez, dışarıdan etkilenmez, taşma yok */
    .gbr-location-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
        gap: 20px;

        &__card {
            display: flex;
            align-items: flex-start;
            gap: 16px;
            padding: 20px;
            background: var(--color-bg-subtle);
            border: 1px solid var(--color-border-default, #e8e8e8);
            border-radius: 12px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
            transition: box-shadow 0.2s ease, border-color 0.2s ease;

            &:hover {
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
                border-color: rgba(66, 133, 244, 0.3);
            }
        }

        &__icon {
            width: 48px;
            height: 48px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(66, 133, 244, 0.25);

            .material-icons {
                font-size: 24px;
                color: #fff;
            }
        }

        &__info {
            flex: 1;
            min-width: 0;
            overflow: hidden;
        }

        &__name {
            margin: 0 0 8px;
            font-size: 15px;
            font-weight: 600;
            line-height: 1.35;
            color: var(--color-text-strong-default);
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }

        &__address {
            display: flex;
            align-items: flex-start;
            gap: 6px;
            margin-bottom: 12px;
            font-size: 13px;
            line-height: 1.45;
            color: var(--color-text-default);
            min-width: 0;

            .material-icons {
                font-size: 16px;
                flex-shrink: 0;
                color: #5f6368;
            }
        }

        &__address-text {
            flex: 1;
            min-width: 0;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }

        &__meta {
            display: flex;
            flex-wrap: wrap;
            gap: 12px 16px;
            font-size: 12px;
            color: var(--color-text-default);
            min-width: 0;
        }

        &__meta-item {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            min-width: 0;
            max-width: 100%;

            span:last-child {
                word-wrap: break-word;
                overflow-wrap: break-word;
                word-break: break-word;
            }

            .material-icons {
                font-size: 14px;
                flex-shrink: 0;
                color: #fbbc05;
            }
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .pagination {
        display: flex;
        justify-content: center;
        margin-top: 24px;
    }
}
</style>
